import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { getOptimizations } from '@/api/clarityLens'
import { pushNotification, useAppDispatch } from '@/store'
import { areFilterParamsValid } from '@/utils/filterParams'
import { OptimizationsItemModel } from '@/types/clarityLens'

type ScatterChartDataModel = {
  data: {
    name: string
    x: number
    y: number
    info: string
  }[]
}

export default function useOptimizations(
  startDate: string,
  endDate: string,
  branches: string,
  itemName: string
) {
  const dispatch = useAppDispatch()
  const { t } = useTranslation()
  const [isFetching, setIsFetching] = useState<boolean>(false)

  const isReady = useMemo(
    () =>
      areFilterParamsValid(startDate, endDate, branches) &&
      Boolean(itemName?.trim()),
    [startDate, endDate, branches, itemName]
  )
  const loading = !isReady || isFetching

  const [optimizationsChartData, setOptimizationsChartData] =
    useState<ScatterChartDataModel>()

  const fetchAllData = useCallback(async () => {
    if (!isReady) {
      setIsFetching(false)
      return
    }
    setIsFetching(true)
    try {
      const { data } = await fetchOptimizationsData(
        branches,
        startDate,
        endDate,
        itemName
      )

      const scatterChart: ScatterChartDataModel = convertToScatterChart(data)
      setOptimizationsChartData(scatterChart)
    } catch (err) {
      const errorInstance = new Error(
        err instanceof Error ? err.message : 'An unknown error occurred'
      )

      dispatch(
        pushNotification({
          type: 'error',
          title: t('common.errors.serverError'),
          description: errorInstance.message,
          duration: 5000,
        })
      )
    } finally {
      setIsFetching(false)
    }
  }, [isReady, branches, startDate, endDate, itemName])

  const refetch = useCallback(() => {
    console.log('Refetching sku breakdown information...')
    return fetchAllData()
  }, [fetchAllData])

  useEffect(() => {
    fetchAllData()
  }, [fetchAllData])

  return {
    optimizationsChartData,
    loading,
    refetch,
  }
}

async function fetchOptimizationsData(
  branches: string,
  startDate: string,
  endDate: string,
  itemName: string
): Promise<{
  data: OptimizationsItemModel[]
}> {
  try {
    const response = await getOptimizations(
      branches,
      startDate,
      endDate,
      itemName
    )

    const data: OptimizationsItemModel[] = response.skus.map((sku) => ({
      skuName: sku.sku_name,
      averageUnitPrice: sku.average_unit_price,
      quantity: sku.quantity,
      mfrUom: sku.mfr_uom,
    }))

    return {
      data,
    }
  } catch (err) {
    throw new Error(
      err instanceof Error ? err.message : 'An unknown error occurred'
    )
  }
}

// Convert SKU breakdown data to scatter chart format
function convertToScatterChart(
  skuData: OptimizationsItemModel[]
): ScatterChartDataModel {
  const sortedData = [...skuData].map((sku) => ({
    name: sku.skuName,
    x: sku.averageUnitPrice,
    y: sku.quantity,
    info: sku.mfrUom,
  }))

  return {
    data: sortedData,
  }
}
